import { ArrowLeft, CheckCheck, Pencil } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { Link, Navigate, useParams } from "react-router-dom";
import remarkGfm from "remark-gfm";
import { Button } from "@/components/ui/button";
import {
  useGrammarRule,
  useGrammarRuleWords,
  type GrammarDetail as GrammarDetailData,
  type GrammarProgressAgg,
} from "@/lib/grammar";
import { useT, type I18nKey } from "@/lib/i18n";
import { useMarkKnown } from "@/lib/reviews";

export function GrammarDetail() {
  const { id } = useParams<{ id: string }>();
  const { data: rule, isLoading, isError, error } = useGrammarRule(id);
  const t = useT();

  if (!id) return <Navigate to="/grammar" replace />;

  if (isLoading) {
    return <p className="text-[13px] text-text-3">{t("common.loading")}</p>;
  }

  if (isError || !rule) {
    return (
      <div className="flex flex-col gap-4">
        <BackButton />
        <p className="text-[13px] text-[color:var(--again)]">
          {error instanceof Error ? error.message : t("grammar_detail.not_found")}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Sticky header — see WordDetail for the rationale; same pattern
          keeps the back button reachable while the body markdown + applies-to
          list + related rules scroll past. */}
      <div className="sticky top-0 z-10 -mx-4 bg-bg/85 px-4 py-2 backdrop-blur-md">
        <div className="-ml-2 flex items-center">
          <BackButton />
        </div>
      </div>

      <div>
        <h1 className="text-[32px] font-bold leading-[1.1] tracking-[-0.02em]">{rule.title}</h1>
        {rule.summary && <p className="mt-2 text-[15px] leading-[1.45] text-text-2">{rule.summary}</p>}
        {rule.tags.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {rule.tags.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center rounded-full border border-border px-2.5 py-0.5 text-[11px] text-text-2"
              >
                #{tag}
              </span>
            ))}
          </div>
        )}
      </div>

      {rule.progress && <ProgressCard progress={rule.progress} />}

      <article className="rounded-[var(--r-lg)] border border-border bg-surface px-4 py-4">
        <div className="md">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{rule.body}</ReactMarkdown>
        </div>
      </article>

      <AppliesToSection ruleId={rule.id} />

      <RelatedRulesSection rules={rule.relatedRules} />

      {/* "Quiz yourself" entry point (Phase 5a) — only shows when the
          rule has authored quiz items. Tutored content; if a rule has
          none yet, we hide the card rather than show "0 cards." */}
      {rule.quizItemCount > 0 && (
        <Link
          to={`/grammar/${rule.id}/practice`}
          className="flex items-center gap-3 rounded-[var(--r-lg)] px-4 py-4 transition-opacity hover:opacity-90"
          style={{ background: "var(--accent-soft)" }}
        >
          <div className="flex-1">
            <div className="text-[14px] font-semibold" style={{ color: "var(--accent)" }}>
              {t("grammar_detail.quiz_yourself_title")}
            </div>
            <div className="mt-0.5 text-[12px] text-text-2">
              {t("grammar_detail.quiz_yourself_subtitle", { n: rule.quizItemCount })}
            </div>
          </div>
          <span
            className="inline-flex items-center gap-1.5 rounded-[var(--r-md)] px-3.5 py-2 text-[13px] font-semibold"
            style={{ background: "var(--accent)", color: "var(--accent-fg)" }}
          >
            <Pencil className="size-3.5" />
            {t("grammar_detail.practice")}
          </span>
        </Link>
      )}

      {/* CTA — mirrors WordDetail's layout slot */}
      <div className="flex gap-2 pt-2">
        <MarkKnownAction rule={rule} />
      </div>
    </div>
  );
}

// Mirror of WordDetail's MarkKnownAction: the most useful single action on
// a rule the user already commands is "I know this" — graduates the rule
// past the SRS queue (POST /reviews/know, targetType GRAMMAR) without
// forcing a study session that would only confirm it. Hides itself once
// the rule is mature (state === "known"); no progress row means "new",
// which still shows the button. Unlike words there is no direction and no
// couple-mature — a rule is a single SRS card.
function MarkKnownAction({ rule }: { rule: GrammarDetailData }) {
  const t = useT();
  const markKnown = useMarkKnown();

  if (rule.progress?.state === "known") return null;

  // Same two-state copy split as words: `_new` = "I already know this
  // topic", `_learning` = "graduate past the remaining reviews."
  const labelKey: I18nKey =
    rule.progress?.state === "learning" ? "grammar_detail.mark_known_learning" : "grammar_detail.mark_known_new";

  function handleClick() {
    const ok = window.confirm(t("grammar_detail.mark_known_confirm", { title: rule.title }));
    if (!ok) return;
    markKnown.mutate({ kind: "GRAMMAR", ruleId: rule.id });
  }

  return (
    <Button onClick={handleClick} disabled={markKnown.isPending} className="flex-1">
      <CheckCheck className="size-4" />
      {markKnown.isPending ? t("common.saving") : t(labelKey)}
    </Button>
  );
}

// Rules linked to this one via `relatedRuleSlugs` — either declared here
// (outgoing) or declared on another rule that names this one (inbound).
// Deduped + sorted server-side; we just render.
function RelatedRulesSection({ rules }: { rules: GrammarDetailData["relatedRules"] }) {
  const t = useT();
  if (rules.length === 0) return null;

  return (
    <section>
      <div className="mb-2 pl-0.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-text-3">
        {t("grammar_detail.related_topics")}
      </div>
      <ul className="flex flex-col gap-2">
        {rules.map((r) => (
          <li key={r.id}>
            <Link
              to={`/grammar/${r.id}`}
              className="flex items-center gap-3 rounded-[var(--r-md)] border border-border bg-surface px-3.5 py-3 transition-colors hover:bg-[color:var(--surface-2)]"
            >
              <div className="min-w-0 flex-1">
                <div className="text-[15px] font-semibold">{r.title}</div>
                {r.summary && <div className="mt-0.5 truncate text-[12px] text-text-3">{r.summary}</div>}
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

// Words this rule applies to. Server returns a preview (≤10 items) plus the
// full match total — we display the total next to the section heading so
// authors know how broadly the rule matches without us rendering hundreds
// of chips. Matching source is attributed per word so the display can
// annotate if useful later.
function AppliesToSection({ ruleId }: { ruleId: string }) {
  const t = useT();
  const { data, isPending } = useGrammarRuleWords(ruleId);
  if (isPending) return null; // secondary panel — no skeleton flash
  if (!data || data.total === 0) return null;

  return (
    <section>
      <div className="mb-2 flex items-baseline justify-between pl-0.5 pr-0.5">
        <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-text-3">
          {t("grammar_detail.applies_to")}
        </div>
        <div className="font-mono text-[11px] text-text-3">{t("grammar_detail.applies_count", { n: data.total })}</div>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {data.items.map((w) => (
          <Link
            key={w.id}
            to={`/words/${w.id}`}
            className="inline-flex items-center rounded-full border border-border bg-surface px-3 py-1 text-[13px] font-medium text-text-2 transition-colors hover:bg-[color:var(--surface-2)] hover:text-text"
          >
            {w.lemma}
          </Link>
        ))}
        {/* "+N more" hint when the preview is capped. Kept as a muted chip
            so it's clearly non-interactive — we don't link it anywhere
            until a dedicated "all words for rule" view exists. */}
        {data.total > data.items.length && (
          <span className="inline-flex items-center rounded-full border border-dashed border-border px-3 py-1 text-[12px] font-medium text-text-3">
            {t("grammar_detail.applies_more", { n: data.total - data.items.length })}
          </span>
        )}
      </div>
    </section>
  );
}

function BackButton() {
  const t = useT();
  return (
    <Link
      to="/grammar"
      aria-label={t("grammar_detail.back_aria")}
      className="inline-flex size-9 items-center justify-center rounded-full text-text-2 transition-colors hover:bg-[color:var(--surface-2)] hover:text-text"
    >
      <ArrowLeft className="size-4" />
    </Link>
  );
}

function ProgressCard({ progress }: { progress: GrammarProgressAgg }) {
  const t = useT();
  const stateLabel = progress.state === "known" ? t("common.known") : t("common.learning");
  const nextReview = formatNextReview(progress.nextReviewAt, t);
  const pct = progressPct(progress.repetitions);

  return (
    <div className="rounded-[var(--r-lg)] border border-border bg-surface p-4">
      <div className="mb-2 flex items-center justify-between">
        <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-text-3">
          {t("word_detail.your_progress")}
        </div>
        <span className="font-mono text-[11px] text-text-3">
          EF {progress.easeFactor.toFixed(1)} · rep {progress.repetitions}
        </span>
      </div>
      <div className="mb-2 flex flex-wrap items-baseline gap-2">
        <span className="text-[15px] font-semibold">{stateLabel}</span>
        <span className="text-[12px] text-text-3">· {nextReview}</span>
      </div>
      <div className="progress-track">
        <div className="fill" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

// Reuses the word_detail.next_review_* strings — same semantic, same format,
// no reason to duplicate the copy across word and grammar namespaces.
function formatNextReview(iso: string, t: ReturnType<typeof useT>): string {
  const next = new Date(iso);
  const now = new Date();
  const diffMs = next.getTime() - now.getTime();
  const diffDays = Math.round(diffMs / (24 * 60 * 60 * 1000));
  if (diffDays < 0) return t("word_detail.next_review_overdue", { n: Math.abs(diffDays) });
  if (diffDays === 0) return t("word_detail.next_review_today");
  if (diffDays < 30) return t("word_detail.next_review_days", { n: diffDays });
  const months = Math.round(diffDays / 30);
  return t("word_detail.next_review_months", { n: months });
}

function progressPct(reps: number): number {
  const steps: Record<number, number> = { 0: 0, 1: 25, 2: 50, 3: 75 };
  return steps[reps] ?? Math.min(100, 75 + (reps - 3) * 5);
}
